/**
 * CancelCreditDialog
 *
 * Modal de confirmação para cancelamento manual de um crédito ativo do cliente.
 * Exige motivo obrigatório (5 a 1000 caracteres), que é gravado em auditoria e
 * exibido na linha do tempo de créditos.
 *
 * RELEASE: 2026-08-19 v1.0.0 - Versão inicial.
 */

import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import { formatCurrency } from '@/lib/currency-utils';
import { useCancelCredit } from '@/hooks/financial/useCancelCredit';

const MOTIVO_MIN = 5;
const MOTIVO_MAX = 1000;

interface CancelCreditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Crédito alvo do cancelamento */
  credito: { id: string; saldo: number; origem: string } | null;
  /** Cliente dono do crédito (invalidação de cache) */
  customerId?: string | null;
  /** Callback opcional após sucesso */
  onSuccess?: () => void;
}

export function CancelCreditDialog({
  open,
  onOpenChange,
  credito,
  customerId,
  onSuccess,
}: CancelCreditDialogProps) {
  const [motivo, setMotivo] = useState('');
  const { mutateAsync, isPending } = useCancelCredit();

  // Limpa o campo sempre que o modal reabre para outro crédito.
  useEffect(() => {
    if (open) setMotivo('');
  }, [open, credito?.id]);

  const motivoValido = motivo.trim().length >= MOTIVO_MIN;

  const handleConfirm = async (e: React.MouseEvent) => {
    // Impede o fechamento automático do AlertDialog antes da conclusão.
    e.preventDefault();
    if (!credito || !motivoValido || isPending) return;

    await mutateAsync({
      creditoId: credito.id,
      motivo: motivo.trim(),
      customerId,
    });

    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Cancelar crédito
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-1 text-sm">
              <p>
                O crédito deixará de estar disponível para novas contratações, mas permanecerá
                registrado no histórico para auditoria. Esta ação não pode ser desfeita.
              </p>
              {credito && (
                <p className="text-foreground">
                  Saldo a cancelar:{' '}
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(credito.saldo)}
                  </span>
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="cancel-credit-motivo" className="text-xs">
            Motivo do cancelamento <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="cancel-credit-motivo"
            value={motivo}
            maxLength={MOTIVO_MAX}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Descreva o motivo do cancelamento deste crédito…"
            className="min-h-[90px] text-sm"
            disabled={isPending}
          />
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Mínimo de {MOTIVO_MIN} caracteres.</span>
            <span className="tabular-nums">
              {motivo.length}/{MOTIVO_MAX}
            </span>
          </div>
          {!motivoValido && motivo.length > 0 && (
            <InlineNotice variant="warning">
              Informe um motivo com pelo menos {MOTIVO_MIN} caracteres.
            </InlineNotice>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Voltar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!motivoValido || isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cancelar crédito
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
